import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { getUserProfileInfo } from '../../services/userProfileService';
import { UserProfileModel } from '../../types/profile.types';

export interface ProfileState {
    loading: boolean;
    userInfo: UserProfileModel | null;
    error: string | null;
}

const defaultProfileState: ProfileState = {
    loading: false,
    userInfo: null,
    error: null,
};

export const fetchUserProfileInfo = createAsyncThunk(
    'profile/fetchUserProfileInfo',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState() as RootState;
        const token = state.auth?.token;

        if (!token) {
            return thunkAPI.rejectWithValue('Not authenticated');
        }

        try {
            const result = await getUserProfileInfo();
            return result as UserProfileModel;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(
                error?.response?.data ?? 'Something went wrong.'
            );
        }
    }
);

export const profileSlice = createSlice({
    name: 'profile',
    initialState: defaultProfileState,
    reducers: {
        clearProfile: () => ({ ...defaultProfileState }),
        updatePreferredLanguageCode: (state, action: { payload: string }) => {
            if (state.userInfo) {
                state.userInfo.preferredLanguageCode = action.payload;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchUserProfileInfo.pending, (state) => {
            state.loading = true;
            state.error = null;
        });

        builder.addCase(fetchUserProfileInfo.rejected, (state, action) => {
            state.loading = false;
            state.userInfo = null;
            state.error = (action.payload as string) ?? 'Something went wrong.';
        });

        builder.addCase(fetchUserProfileInfo.fulfilled, (state, action) => {
            state.loading = false;
            state.userInfo = action.payload as UserProfileModel;
            state.error = null;
        });
    },
});

export const { clearProfile, updatePreferredLanguageCode } = profileSlice.actions;

export const profileSelector = (state: RootState) => state.profile;
